<template>
  <div v-if="mode === 'map'" class="relative h-full">
    <GoogleMap
      :api-key="googleMapApiKey"
      style="width: 100%; height: 100%"
      :center="center"
      :zoom="15"
    >
      <Marker
        ref="marker"
        :options="{
          position: center,
          draggable: true
        }"
        @dragend="handleDragEndMarker"
      />
    </GoogleMap>
    <div
      class="absolute bottom-2 left-2 aspect-video w-1/4 overflow-hidden rounded-lg"
    >
      <button
        class="absolute top-2 right-2 aspect-square w-5 rounded-full bg-white"
        @click="mode = 'streetview'"
      >
        <FontAwesomeIcon
          :icon="['fas', 'arrow-up-right-and-arrow-down-left-from-center']"
          size="xs"
        />
      </button>
      <img
        v-if="srcStreetview"
        :src="srcStreetview"
        class="inset-0 object-cover"
      />
    </div>
  </div>
  <div v-else class="relative h-full">
    <StreetViewComponent
      v-model:longitude="localLongitude"
      v-model:latitude="localLatitude"
      v-model:streetview="localStreetview"
    />
    <div
      class="absolute bottom-2 left-2 z-10 aspect-video w-1/4 overflow-hidden rounded-lg"
    >
      <button
        class="absolute top-2 right-2 aspect-square w-5 rounded-full bg-white"
        @click="mode = 'map'"
      >
        <FontAwesomeIcon
          :icon="['fas', 'arrow-up-right-and-arrow-down-left-from-center']"
          size="xs"
        />
      </button>
      <img
        v-if="srcMap"
        :src="srcMap"
        class="inset-0 object-cover"
        @click="mode = 'map'"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { GoogleMap, Marker } from 'vue3-google-map'
import StreetViewComponent from '@c/Maps/StreetViewComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default defineComponent({
  name: 'LocationSelectorComponent',
  components: { FontAwesomeIcon, StreetViewComponent, GoogleMap, Marker },
  props: {
    latitude: {
      type: [Number, String],
      required: true
    },
    longitude: {
      type: [Number, String],
      required: true
    },
    streetview: {
      type: Array,
      required: false,
      default: null
    },
    centerLatitude: {
      type: [Number, String],
      required: false,
      default: null
    },
    centerLongitude: {
      type: [Number, String],
      required: false,
      default: null
    }
  },
  emits: ['update:latitude', 'update:longitude', 'update:streetview'],
  data() {
    return {
      mode: 'map'
    }
  },
  computed: {
    localLatitude: {
      get() {
        return this.latitude
      },
      set(value) {
        this.$emit('update:latitude', value)
      }
    },
    localLongitude: {
      get() {
        return this.longitude
      },
      set(value) {
        this.$emit('update:longitude', value)
      }
    },
    localStreetview: {
      get() {
        return this.streetview
      },
      set(value) {
        this.$emit('update:streetview', value)
      }
    },
    center() {
      if (this.centerLatitude !== null && this.centerLongitude !== null) {
        return {
          lat: parseFloat(this.centerLatitude),
          lng: parseFloat(this.centerLongitude)
        }
      } else if (this.latitude !== null && this.longitude !== null) {
        return {
          lat: parseFloat(this.latitude),
          lng: parseFloat(this.longitude)
        }
      }
      return undefined
    },
    srcStreetview() {
      if (
        this.streetview &&
        this.streetview.latitude &&
        this.streetview.longitude
      ) {
        return `https://maps.googleapis.com/maps/api/streetview?size=480x270&scale=2&key=${this.googleMapApiKey}&return_error_code=true&fov=${this.streetview.fov}&zoom=${this.streetview.zoom}&pitch=${this.streetview.pitch}&heading=${this.streetview.heading}&location=${this.streetview.latitude},${this.streetview.longitude}`
      }
      return null
    },
    srcMap() {
      if (this.latitude && this.longitude) {
        return `https://maps.googleapis.com/maps/api/staticmap?center=${this.latitude},${this.longitude}&size=480x270&sensor=true&zoom=14&markers=${this.latitude},${this.longitude}&scale=2&key=${this.googleMapApiKey}&return_error_code=true`
      }
    },
    googleMapApiKey() {
      return import.meta.env.VITE_GOOGLE_MAP_API_KEY
    }
  },
  methods: {
    handleDragEndMarker(evt) {
      let latitude = evt.latLng.lat().toFixed(8)
      let longitude = evt.latLng.lng().toFixed(8)
      this.localLatitude = latitude
      this.localLongitude = longitude
      if (this.streetview) {
        this.localStreetview.latitude = latitude
        this.localStreetview.longitude = longitude
        this.localStreetview.pov = 0
        this.localStreetview.heading = 0
        this.localStreetview.pitch = 0
      }
    }
  }
})
</script>
